import { HIDDEN_SELECTOR } from '../utils/keyboard-nav-support';
import { registerDestructor } from '@ember/destroyable';
import Modifier from 'ember-modifier';

// Marks the `<strong>` elements this highlighter creates so `stripBolding` can
// unwrap only its own matches and never touch a `<strong>` authored in a
// consumer's template (e.g. an "Autopay <strong>on</strong>" menu item). Removing
// a template-owned node out from under Glimmer corrupts its bounds and crashes
// the next re-render/teardown with "removeChild ... not a child".
const HIGHLIGHT_ATTR = 'data-spds-highlight';

// Collapse runs of spaces without replacing the node itself. Mutating
// `nodeValue` in place preserves the node identity Glimmer tracks; assigning
// `parentNode.textContent` (as this used to) would blow away every child node —
// including ones Glimmer still holds references to — and corrupt teardown.
const collapseSpaces = function (element) {
  let walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
  let textNode;

  while ((textNode = walker.nextNode())) {
    let collapsed = textNode.nodeValue.replace(/ +/g, ' ');

    if (collapsed !== textNode.nodeValue) textNode.nodeValue = collapsed;
  }
};

// Non-destructive un-bolding: unwrap only the `<strong>` elements we added
// (moving their children back into place) and let `normalize()` merge the split
// text nodes back into the original node. This never removes or replaces the
// text nodes Glimmer rendered, so re-render and teardown stay crash-free.
export const stripBolding = function (element) {
  if (!element) return;

  let highlights = element.querySelectorAll(`strong[${HIGHLIGHT_ATTR}]`);

  // Nothing we added — leave the consumer's template DOM (including any of its
  // own `<strong>` elements and text-node boundaries) exactly as Glimmer
  // rendered it. Running `normalize()`/`collapseSpaces` here would merge or
  // mutate nodes Glimmer still tracks and break its next render.
  if (highlights.length === 0) return element.textContent?.trim();

  highlights.forEach(strong => {
    let parentNode = strong.parentNode;

    if (!parentNode) return;

    while (strong.firstChild) {
      parentNode.insertBefore(strong.firstChild, strong);
    }

    parentNode.removeChild(strong);
  });

  element.normalize();
  collapseSpaces(element);

  return element.textContent?.trim();
};

const createWalker = element =>
  element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);

const getNodes = walker => {
  let allNodes = [];
  let textNode;

  while ((textNode = walker.nextNode())) {
    if (textNode.nodeValue.trim() === '') {
      continue;
    }

    allNodes.push(textNode);
  }

  return allNodes;
};

const processNodes = (allNodes, upperSearchTerm) => {
  let matches = [];

  allNodes.forEach(textNode => {
    let originalNodeValue = textNode.nodeValue;
    let trimmedNodeValue = originalNodeValue.trim();
    let upperText = trimmedNodeValue.toUpperCase();

    if (upperText.includes(upperSearchTerm)) {
      let position = upperText.indexOf(upperSearchTerm);
      let match = {
        offset: originalNodeValue.indexOf(trimmedNodeValue),
        positions: [position],
        textNode,
        trimmedNodeLength: trimmedNodeValue.length,
      };

      while ((position = upperText.indexOf(upperSearchTerm, position + 1)) !== -1) {
        match.positions.push(position);
      }

      matches.push(match);
    }
  });

  return matches;
};

const createAndPopulateStrongElement = ({
  element,
  upperSearchTerm,
  matchText,
  position,
  needsLeadingSpace,
  needsTrailingSpace,
  trimmedLength,
}) => {
  let isAtBeginning = position === 0;
  let isAtEnd = position + upperSearchTerm.length >= trimmedLength;
  let prefix = needsLeadingSpace && !isAtBeginning ? ' ' : '';
  let strongElement = element.ownerDocument.createElement('strong');
  let suffix = needsTrailingSpace && !isAtEnd ? ' ' : '';

  strongElement.setAttribute(HIGHLIGHT_ATTR, '');
  strongElement.style.whiteSpace = 'pre-wrap';
  strongElement.textContent = prefix + matchText + suffix;

  return strongElement;
};

const processSingleNode = (
  element,
  upperSearchTerm,
  { offset, positions, textNode, trimmedNodeLength }
) => {
  let matchLength = upperSearchTerm.length;

  positions.reverse().forEach(position => {
    let actualPosition = offset + position;
    let originalTextContent = textNode.nodeValue;
    let beforeMatchPos = actualPosition - 1;
    let afterMatchPos = actualPosition + matchLength;
    let matchStartNode = textNode.splitText(actualPosition);

    matchStartNode.splitText(Math.min(matchLength, matchStartNode.nodeValue.length));

    let matchText = matchStartNode.nodeValue;
    let needsLeadingSpace = false;
    let needsTrailingSpace = false;

    if (beforeMatchPos >= 0 && actualPosition > 0) {
      needsLeadingSpace = originalTextContent[beforeMatchPos] === ' ' && !matchText.startsWith(' ');
    }

    if (afterMatchPos < originalTextContent.length) {
      needsTrailingSpace = originalTextContent[afterMatchPos] === ' ' && !matchText.endsWith(' ');
    }

    let strongElement = createAndPopulateStrongElement({
      element,
      upperSearchTerm,
      matchText,
      position,
      needsLeadingSpace,
      needsTrailingSpace,
      trimmedNodeLength,
    });

    matchStartNode.parentNode.replaceChild(strongElement, matchStartNode);
  });
};

/**
 * Wraps occurrences of `searchTerm` within `rootElement` in `<strong>` tags.
 * Shared by the `bold-matching-text` modifier and any component (e.g. menus) that
 * needs to highlight matches against DOM elements it does not own in its template.
 */
export function applyBoldMatchingText(rootElement, searchTerm) {
  // `undefined` means "not managed here" (e.g. a filterable menu owns
  // highlighting); leave any existing bolding untouched. An empty string, by
  // contrast, is an explicit "clear" and must strip previous matches.
  if (searchTerm === undefined) return;

  let element = rootElement?.querySelector('span') ?? rootElement;

  if (!element) return;

  stripBolding(element);

  if (!searchTerm || element.classList.contains(HIDDEN_SELECTOR)) return;

  let upperSearchTerm = searchTerm.trim().toUpperCase();

  let allNodes = getNodes(createWalker(element));

  processNodes(allNodes, upperSearchTerm)
    .reverse()
    .forEach(match => processSingleNode(element, upperSearchTerm, match));
}

export default class BoldMatchingText extends Modifier {
  #element;

  constructor(owner, args) {
    super(owner, args);
    // Strip synchronously before Glimmer tears the element down, so teardown
    // always sees the original, un-mutated DOM structure.
    registerDestructor(this, () => stripBolding(this.#element));
  }

  modify(element, [searchTerm]) {
    this.#element = element;
    applyBoldMatchingText(element, searchTerm);
  }
}
